import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from 'components/Button';
import Input from 'components/Input';
import FormContainer from 'components/LoanForm/FormContainer';
import { useForm } from 'react-hook-form';
import { getMessageForInvalidFields, getMessageForRequiredFields } from 'utils/errors';
import UserSessionWarning from 'components/Common/UserSessionWarning/UserSessionWarning';
import FormNavigation from 'components/FormNavigation';
import { useDispatch, useSelector } from 'react-redux';
import { setYourContactData, ContactType } from 'handlers/yourContact';
import { getYourContactData } from 'selectors/getYourContact';
import { YourContactVariable } from 'enums/LoanFormVariables';
import { RoutePath } from 'enums/Routes';
import { FlowComponentType } from 'routes/types';
import { getNewInputValue } from 'utils/inputHelpers';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useCurrentFlow from 'hooks/useCurrentFlow';
import { useResendLogic } from 'hooks/useResendLogic';
import { sendEmailCode } from 'thunks';

import styles from './YourEmail.module.scss';

export const EMAIL_PATTERN = /^[a-zA-Z0-9_.-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+$/;

export enum YourContactInputLabel {
  Email = 'Email',
}

export interface YourEmailState {
  isDifferentEmailSession?: boolean;
}

const YourEmail = ({ handleNext, navigationInfo }: FlowComponentType) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isCardFlow } = useCurrentFlow();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const isDifferentEmailSession = (location.state as YourEmailState)?.isDifferentEmailSession;
  const { isTimerActive, canResend, recordSent } = useResendLogic(ContactType.Email);

  const { phone_number: phoneNumber, email } = useSelector(getYourContactData);
  const [shouldContinue, setShouldContinue] = useState<boolean>(false);
  const [isSendingCode, setIsSendingCode] = useState<boolean>(false);

  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      email: isDifferentEmailSession ? '' : email,
    },
  });

  const watcher = watch();

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = getNewInputValue(watcher[YourContactVariable.Email], event.target.value.trim());
    setValue(YourContactVariable.Email, newValue);
    trigger(YourContactVariable.Email);
  };

  const onBlur = () => {
    trigger(YourContactVariable.Email);
  };

  const handleContinue = async () => {
    dispatch(setYourContactData(watcher));
    const currentEmail = watcher[YourContactVariable.Email]!;

    if (isTimerActive && email === currentEmail) {
      setShouldContinue(true);
      return;
    }

    if (canResend || email !== currentEmail) {
      setIsSendingCode(true);
      await dispatchWithUnwrap(sendEmailCode({ phoneNumber, email: currentEmail }));
      recordSent(email!);
      setIsSendingCode(false);
    }

    setShouldContinue(true);
  };

  useEffect(() => {
    register(YourContactVariable.Email, {
      required: getMessageForRequiredFields(YourContactInputLabel.Email),
      pattern: {
        message: getMessageForInvalidFields(YourContactInputLabel.Email),
        value: EMAIL_PATTERN,
      },
    });
  }, [register]);

  useEffect(() => {
    if (shouldContinue) {
      handleNext();
    }
  }, [shouldContinue]);

  return (
    <>
      <FormNavigation
        {...navigationInfo}
        {...(isCardFlow && { title: 'Sign Up' })}
        {...(isDifferentEmailSession && { onBackClick: () => navigate(RoutePath.YourName) })}
      />
      <FormContainer
        title="Access your account"
        subtitle="Return to your account in the future by providing your email."
      >
        <Input
          value={watcher[YourContactVariable.Email]}
          autoCapitalize="none"
          className={styles.formInput}
          onChange={onChange}
          onBlur={onBlur}
          label={YourContactInputLabel.Email}
          placeholder="youremail@mail.com"
          errorMessage={errors[YourContactVariable.Email]?.message}
          autoComplete="email"
          onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
          autoFocus={!isDifferentEmailSession}
          disabled={isSendingCode}
        />
        <Button className={styles.button} disabled={!isValid} onClick={handleContinue} isLoading={isSendingCode}>
          Next
        </Button>
        {isDifferentEmailSession && (
          <UserSessionWarning
            showContactUsOption
            message="Sorry, it appears the email address you entered is different from the one you originally provided. Please try again"
          />
        )}
      </FormContainer>
    </>
  );
};

export default YourEmail;
